import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { LazyMotion } from 'motion/react';
import { ApiError, onSessionExpired } from '../lib/api.js';
import { loadMotionFeatures } from '../lib/motion.js';
import { useSession } from '../stores/session.js';

/**
 * Everything the tree needs, in one place.
 *
 * TanStack Query owns all server state. The retry policy is the part worth
 * reading: a 4xx is the server telling us the request was wrong, and retrying it
 * three times just makes the same mistake at speed. Only network and 5xx
 * failures are worth another attempt.
 */

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (attempt, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return attempt < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // A mutation retried automatically can place two orders. Never.
        retry: false,
      },
    },
  });

export const Providers = ({ children }) => {
  const [client] = useState(createQueryClient);
  const signedOut = useSession((state) => state.signedOut);

  useEffect(() => {
    // A session that could not be refreshed means the shopper is a guest again.
    // Their cart survives — it is keyed by session cookie, not by account.
    const unsubscribe = onSessionExpired(() => {
      signedOut();
      client.removeQueries({ queryKey: ['account'] });
    });
    return unsubscribe;
  }, [client, signedOut]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={client}>
        {/* The animation feature set is fetched after first paint. Until it
            arrives, `m` elements render as plain DOM at their final state —
            which is the correct fallback, not a broken one. */}
        <LazyMotion features={loadMotionFeatures} strict>
          {children}
        </LazyMotion>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default Providers;
